import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AssetDTO, EntityRefUtil, FormType, GetAssetsRequestDTO, GetAssetsResponseDTO, getSchedulePlanLimit, GetUnitsResponseDTO } from '@gearbelt/global';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { GearbeltAssetType, GearbeltPlanType } from '@site-mate/sitemate-global-shared/lib';
import { useQuery } from '@tanstack/react-query';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { formatDistanceToNow, isToday, startOfDay, subDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { match } from 'ts-pattern';

import { FormBadge } from '@/components/form/form-badge';
import { CheckCircleIcon } from '@/components/icons/check-circle';
import ExclamationIcon from '@/components/icons/exclamation';
import WarningTriangleIcon from '@/components/icons/warning-triangle';
import { ScheduleBadge } from '@/components/schedules/schedule-badge';
import { assetTagsColumnOptions } from '@/components/table-page/columns/asset-tags-column';
import { TagBadges } from '@/components/table-page/columns/tag-badges';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { cn, initials } from '@/lib/utils';
import { getSitemateStartAccountQueryOptions } from '@/queries/account';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAssetQrUrl } from '@/utils/asset';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { formatRateCellValue } from '@/utils/currency';
import { getProcessedForms, getProcessedSchedules, getScheduleLimitWarning, getUniqueFormsByType, ProcessedForm } from '@/utils/deployment-rules';
import { getWorkspaceRoute } from '@/utils/routes';

const columnHelper = createColumnHelper<GetAssetsResponseDTO['data'][number]>();

export const createAssetTableColumns = (opts?: { units?: GetUnitsResponseDTO['data']; canManageAssets?: boolean }) =>
  [
    (opts?.canManageAssets ?? true) &&
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <div className="flex w-full justify-center">
            <label className="-m-2 p-2">
              <Checkbox
                checked={table.getIsAllPageRowsSelected()}
                onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                aria-label="Select all"
              />
            </label>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex w-full justify-center">
            <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
              <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
            </label>
          </div>
        ),
        meta: {
          cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
        },
        enableSorting: false,
        enableHiding: false,
      }),
    /*
      We're forced to use an accessor here even though it technically doesn't exist.
      Existing issue: https://github.com/TanStack/table/issues/4136
    */
    columnHelper.accessor('internalId', {
      id: 'label',
      header: 'Asset',
      enableGrouping: true,
      cell: ({ row }) => {
        const { internalId, manufacturer, class: assetClass, category, registration } = row.original;
        const label = EntityRefUtil.createAssetLabel({
          internalId: internalId,
          manufacturer: manufacturer,
          class: assetClass,
          category: category,
          registration: registration,
        });

        return <span>{label}</span>;
      },
    }),
    columnHelper.accessor('internalId', {
      id: 'internalId',
      header: 'Asset ID',
      enableGrouping: false,
      enableColumnFilter: true,
      meta: {
        filterType: 'text',
      },
    }),
    columnHelper.accessor('operatedBy', {
      header: 'Operator',
      cell: ({ row }) => {
        const asset = row.original;
        const fullName = EntityRefUtil.createUserFullNameRef(asset.operatedBy);
        const value = fullName || '-';
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(asset.operatedBy?.firstName || '');

        const operatedByFullInitials = initials(fullName);
        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={operatedByFullInitials}
            avatarUrl={asset.operatedBy?.avatarUrl || ''}
            avatarBackgroundColorClass={avatarBackgroundColorClass}
          />
        );
      },
      getGroupingValue: (row) => EntityRefUtil.createUserFullNameRef(row.operatedBy, 'None'),
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })).json();

          const resultsMap = new Map(data.map((user) => [user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds, userType: 'sitemate-users' },
                })
              ).json();

              for (const user of missingUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor('location', {
      header: 'Location',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value.name}</span> : <span className="text-secondary-text">-</span>;
      },
      getGroupingValue: (row) => row.location?.name || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetLocations({ limit: 100, ...filters })).json();

          const resultsMap = new Map(data.map((location) => [location.id, { label: location.name, value: location.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingLocations } = await (
                await ApiService.GetLocations({
                  limit: 100,
                  filters: { fields: { id: missingIds } },
                })
              ).json();

              for (const location of missingLocations) {
                resultsMap.set(location.id, { label: location.name, value: location.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor((row) => row.assetLocation?.recordedAt, {
      id: 'lastLocationAt',
      header: 'Last Location At',
      enableGrouping: false,
      enableSorting: true,
      cell: ({ row }) => {
        const assetLocation = row.original.assetLocation;
        if (!assetLocation) return <span className="text-secondary-text">Never</span>;
        const parsedValue = new Date(assetLocation.recordedAt);

        let status: 'danger' | 'warning' | 'success' = 'danger';
        if (subDays(startOfDay(new Date()), 7).getTime() < parsedValue.getTime()) status = 'warning';
        if (startOfDay(new Date()).getTime() < parsedValue.getTime()) status = 'success';

        const timeAgo = isToday(parsedValue) ? 'Today' : formatDistanceToNow(parsedValue);

        const icon = match(status)
          .with('success', () => <CheckCircleIcon className="text-default-green" />)
          .with('warning', () => <WarningTriangleIcon className="text-default-orange" />)
          .otherwise(() => <ExclamationIcon className="text-default-red" />);

        return (
          <span className="flex items-center gap-1">
            {icon} {timeAgo}
          </span>
        );
      },
    }),
    columnHelper.accessor('type', {
      header: 'Asset Type',
      cell: ({ getValue }) => getValue() || '-',
      getGroupingValue: (row) => row.type || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'multi-select',
        getFilterOptionsFn: async () => {
          return Object.values(GearbeltAssetType).map((label) => ({
            label,
            value: label,
          }));
        },
      },
    }),
    columnHelper.accessor('category', {
      header: 'Asset Category',
      cell: ({ getValue }) => getValue()?.name || '-',
      getGroupingValue: (row) => row.category?.name || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetAssetCategories({ limit: 1000, ...filters })).json();

          const resultsMap = new Map(data.map((assetCategory) => [assetCategory.id, { label: assetCategory.name, value: assetCategory.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingAssetCategories } = await (
                await ApiService.GetAssetCategories({
                  limit: 1000,
                  filters: { fields: { id: missingIds } },
                })
              ).json();

              for (const assetCategory of missingAssetCategories) {
                resultsMap.set(assetCategory.id, { label: assetCategory.name, value: assetCategory.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.display({
      id: 'assetUrl',
      header: 'Asset URL',
      cell: ({ row }) => {
        const qrCodeValue = row.original.qrCodes?.[0]?.value;
        return qrCodeValue ? <span>{getAssetQrUrl(qrCodeValue)}</span> : <span className="text-secondary-text">-</span>;
      },
      enableSorting: false,
    }),
    columnHelper.accessor('manufacturer', {
      header: 'Manufacturer',
      cell: ({ getValue }) => getValue() || '-',
      getGroupingValue: (row) => row.manufacturer || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async () => {
          const response = await (await ApiService.GetManufacturers({ limit: 500 })).json();
          return response.data.map((manufacturer) => ({
            label: manufacturer.make,
            value: manufacturer.id,
          }));
        },
      },
    }),
    columnHelper.accessor('model', {
      header: 'Model',
      cell: ({ getValue }) => getValue() || '-',
      getGroupingValue: (row) => row.model || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'text',
      },
    }),
    columnHelper.accessor('class', {
      header: 'Class',
      cell: ({ getValue }) => getValue() || '-',
      getGroupingValue: (row) => row.class || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'text',
      },
    }),
    columnHelper.accessor('year', {
      header: 'Year',
      cell: ({ getValue }) => getValue() || '-',
      getGroupingValue: (row) => row.year?.toString() || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'multi-select',
        getFilterOptionsFn: async () => {
          return Array.from({ length: new Date().getFullYear() - 1899 }, (_, i) => 1900 + i)
            .reverse()
            .map((year) => ({
              label: year.toString(),
              value: year.toString(),
            }));
        },
      },
    }),
    columnHelper.accessor('registration', {
      header: 'Registration',
      cell: ({ getValue }) => getValue() || '-',
      enableGrouping: false,
      enableColumnFilter: true,
      meta: {
        filterType: 'text',
      },
    }),
    columnHelper.accessor('serialNumber', {
      header: 'Serial Number',
      cell: ({ getValue }) => getValue() || '-',
      enableGrouping: false,
      enableColumnFilter: true,
      meta: {
        filterType: 'text',
      },
    }),
    columnHelper.accessor('engineNumber', {
      header: 'Engine Number',
      cell: ({ getValue }) => getValue() || '-',
      enableGrouping: false,
      enableColumnFilter: true,
      meta: {
        filterType: 'text',
      },
    }),
    columnHelper.accessor('chassisVin', {
      header: 'Chassis/VIN',
      cell: ({ getValue }) => getValue() || '-',
      enableGrouping: false,
      enableColumnFilter: true,
      meta: {
        filterType: 'text',
      },
    }),
    columnHelper.accessor('rate', {
      header: 'Rate',
      cell: ({ getValue }) => {
        const value = getValue();
        const formatted = formatRateCellValue(value);
        if (!formatted) return <span className="text-secondary-text">-</span>;
        return <span>{formatted}</span>;
      },
      enableGrouping: false,
      enableSorting: true,
    }),
    columnHelper.accessor('comments', {
      id: 'comments',
      header: 'Comments',
      cell: ({ getValue }) => {
        const comments = getValue();
        return comments ? <span className="truncate">{comments}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: false,
      enableSorting: false,
    }),
    ...(opts?.units
      ? opts.units.flatMap((unit) => [
          columnHelper.accessor((row) => row.unitReadings?.find((reading) => reading.unitId === unit.id)?.reading, {
            id: `unitReading-${unit.id}`,
            header: `Last Reading ${unit.abbreviation}`,
            enableGrouping: false,
            enableSorting: true,
            cell: ({ row }) => {
              const reading = row.original.unitReadings?.find((reading) => reading.unitId === unit.id);
              return reading ? (
                <span>
                  {reading?.reading.toLocaleString('en-US', { maximumFractionDigits: 0 })} <span className="text-secondary-text">{unit?.abbreviation}</span>
                </span>
              ) : (
                <span className="text-secondary-text">-</span>
              );
            },
          }),
          columnHelper.accessor((row) => row.unitReadings?.find((reading) => reading.unitId === unit.id)?.recordedAt, {
            id: `unitReadingAt-${unit.id}`,
            header: `Last Reading ${unit.abbreviation} at`,
            enableGrouping: false,
            enableSorting: true,
            cell: ({ row }) => {
              const reading = row.original.unitReadings?.find((reading) => reading.unitId === unit.id);
              if (!reading) return <span className="text-secondary-text">Never</span>;
              const parsedValue = new Date(reading.recordedAt);

              let status: 'danger' | 'warning' | 'success' = 'danger';
              if (subDays(startOfDay(new Date()), 7).getTime() < parsedValue.getTime()) status = 'warning';
              if (startOfDay(new Date()).getTime() < parsedValue.getTime()) status = 'success';

              const timeAgo = isToday(parsedValue) ? 'Today' : formatDistanceToNow(parsedValue);

              const icon = match(status)
                .with('success', () => <CheckCircleIcon className="text-default-green" />)
                .with('warning', () => <WarningTriangleIcon className="text-default-orange" />)
                .otherwise(() => <ExclamationIcon className="text-default-red" />);

              return (
                <span className="flex items-center gap-1">
                  {icon} {timeAgo}
                </span>
              );
            },
          }),
        ])
      : []),
    columnHelper.accessor('tags', {
      ...assetTagsColumnOptions,
      cell: ({ getValue }) => <TagBadges tags={getValue()} />,
    }),
    columnHelper.accessor('forms', {
      id: 'inspectionForms',
      header: 'Inspection Forms',
      cell: ({ getValue }) => {
        const forms = getValue()?.filter((form) => form.type === FormType.InspectionForm);
        return forms && forms.length > 0 ? <FormBadges forms={forms} /> : <span className="text-secondary-text">-</span>;
      },
      enableSorting: false,
      meta: {
        cellClassName: 'max-w-[400px]',
      },
    }),
    columnHelper.accessor('forms', {
      id: 'repairForms',
      header: 'Repair Forms',
      cell: ({ getValue }) => {
        const forms = getValue()?.filter((form) => form.type === FormType.RepairForm);
        return forms && forms.length > 0 ? <FormBadges forms={forms} /> : <span className="text-secondary-text">-</span>;
      },
      enableSorting: false,
      meta: {
        cellClassName: 'max-w-[400px]',
      },
    }),
    columnHelper.display({
      id: 'schedules',
      header: 'Schedules',
      cell: ({ row }) => {
        const schedules = row.original.schedules;
        return schedules && schedules.length > 0 ? (
          <ScheduleBadges schedules={schedules} assetType={row.original.type as GearbeltAssetType} />
        ) : (
          <span className="text-secondary-text">-</span>
        );
      },
      enableSorting: false,
      meta: {
        cellClassName: 'max-w-[400px]',
      },
    }),
  ].filter(Boolean) as AccessorKeyColumnDef<AssetDTO>[];

export const groupOptions = [
  { label: 'None', accessor: '' },
  ...createAssetTableColumns()
    .filter((column) => ['operatedBy', 'location', 'type', 'category', 'manufacturer', 'model', 'class', 'year'].includes(column.accessorKey))
    .map((column) => ({ label: column.header?.toString() || '', accessor: column.accessorKey })),
] satisfies { label: string; accessor: string }[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<ReturnType<typeof createAssetTableColumns>[number]['id'] | ReturnType<typeof createAssetTableColumns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetAssetsRequestDTO['filters']>['fields']>
> = {
  internalId: 'internalId',
  operatedBy: 'operatedById',
  location: 'locationId',
  type: 'type',
  category: 'categoryId',
  manufacturer: 'manufacturer',
  model: 'model',
  class: 'class',
  year: 'year',
  registration: 'registration',
  serialNumber: 'serialNumber',
  engineNumber: 'engineNumber',
  chassisVin: 'chassisVin',
  tags: 'tagIds',
};

const FormBadges = ({ forms, className }: { forms?: GetAssetsResponseDTO['data'][number]['forms']; className?: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(forms?.length || 0);

  useLayoutEffect(() => {
    if (!containerRef.current || !forms?.length) return;

    const container = containerRef.current;
    const MAX_WIDTH = 400 - 63; // Subtract width for "+ x more..." text

    // Measure synchronously after render but before paint
    const badges = Array.from(container.querySelectorAll('[data-badge]'));
    let totalWidth = 0;
    let count = 0;

    for (const badge of badges) {
      const badgeWidth = badge.getBoundingClientRect().width;
      totalWidth += badgeWidth + 4; // Add 4px for gap
      if (totalWidth > MAX_WIDTH) break;
      count++;
    }

    // Only update if the count actually changed to prevent unnecessary re-renders
    if (count !== visibleCount) {
      setVisibleCount(count);
    }
  }, [forms?.length, visibleCount]);

  if (!forms?.length) return null;

  // For schedule and repair forms, we want to show only the form with the latest deployment rule
  // For inspection forms, we show all forms
  const formType = forms[0]?.type;
  let processedForms = [...forms] as ProcessedForm[];

  if (formType === FormType.ScheduleForm || formType === FormType.RepairForm) {
    processedForms = getProcessedForms(forms, formType);
  } else if (formType === FormType.InspectionForm) {
    processedForms = getUniqueFormsByType(forms, formType);
  }

  const visibleForms = processedForms.slice(0, visibleCount);
  const hiddenCount = processedForms.length - visibleCount;

  return (
    <div ref={containerRef} className={cn('flex max-w-[400px] gap-1', className)}>
      <div className="flex gap-1">
        {visibleForms.map((form) => {
          const deploymentRules = forms
            .filter((f) => f.id === form.id && f.deploymentRule)
            .map((f) => f.deploymentRule!)
            .filter((rule, index, self) => index === self.findIndex((r) => r.id === rule.id));

          const displayRules = deploymentRules.slice(0, 3);
          const remainingRules = deploymentRules.length - 3;

          // Determine if this form should use the grey badge
          const isSecondary = (formType === FormType.ScheduleForm || formType === FormType.RepairForm) && !form.isPrimary;

          return (
            <TooltipProvider key={form.id}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <div className="flex" data-badge>
                    <FormBadge form={form} disabled={isSecondary} className="text-xs" />
                  </div>
                </TooltipTrigger>

                <TooltipPortal>
                  <TooltipContent side="bottom" sideOffset={0} className="bg-default-text max-w-[200px] border-none p-1 text-xs text-white">
                    <TooltipArrow className="fill-default-text" />
                    {deploymentRules.length > 0 ? (
                      <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                        {isSecondary && (
                          <div>
                            <b>Warning:</b> This form cannot be used because only 1 {getFormTypeText(formType)} form can be deployed to each asset.
                          </div>
                        )}

                        <div>
                          <b>Rules:</b>

                          <ul className="list-disc pl-4">
                            {displayRules.map((rule) => (
                              <li key={rule.id} className="hover:underline">
                                <Link className="block w-full truncate" to={getWorkspaceRoute(`/deployments/${rule.id}`)}>
                                  {rule.name}
                                </Link>
                              </li>
                            ))}
                            {remainingRules > 0 && <li>+{remainingRules} more...</li>}
                          </ul>
                        </div>
                      </div>
                    ) : (
                      <span>No deployment rules</span>
                    )}
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
      {hiddenCount > 0 && <span className="text-secondary-text text-sm">+{hiddenCount} more...</span>}
    </div>
  );
};

type ScheduleBadgesProps = {
  schedules?: GetAssetsResponseDTO['data'][number]['schedules'];
  assetType?: GearbeltAssetType;
  className?: string;
};

const ScheduleBadges = ({ schedules, className, assetType }: ScheduleBadgesProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(schedules?.length || 0);

  // Get plan type and schedule limit from account service
  const { org } = useUserStore();
  const { data: account } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));
  const planType = account?.billing?.gearbelt?.planType as GearbeltPlanType | undefined;
  const schedulePlanLimit = getSchedulePlanLimit(planType);

  useLayoutEffect(() => {
    if (!containerRef.current || !schedules?.length) return;

    const container = containerRef.current;
    const MAX_WIDTH = 400 - 63; // Subtract width for "+ x more..." text

    // Measure synchronously after render but before paint
    const badges = Array.from(container.querySelectorAll('[data-badge]'));
    let totalWidth = 0;
    let count = 0;

    for (const badge of badges) {
      const badgeWidth = badge.getBoundingClientRect().width;
      totalWidth += badgeWidth + 4; // Add 4px for gap
      if (totalWidth > MAX_WIDTH) break;
      count++;
    }

    // Only update if the count actually changed to prevent unnecessary re-renders
    if (count !== visibleCount) {
      setVisibleCount(count);
    }
  }, [schedules?.length, visibleCount]);

  const processedSchedules = useMemo(() => getProcessedSchedules(schedules, schedulePlanLimit), [schedules, schedulePlanLimit]);

  const visibleSchedules = processedSchedules.slice(0, visibleCount);
  const hiddenCount = processedSchedules.length - visibleCount;

  if (!schedules?.length) return null;

  return (
    <div ref={containerRef} className={cn('flex max-w-[400px] gap-1', className)}>
      <div className="flex gap-1">
        {visibleSchedules.map((schedule) => {
          const deploymentRules = schedules
            .filter((f) => f.id === schedule.id && f.deploymentRule)
            .map((f) => f.deploymentRule!)
            .filter((rule, index, self) => index === self.findIndex((r) => r.id === rule.id));

          const displayRules = deploymentRules.slice(0, 3);
          const remainingRules = deploymentRules.length - 3;

          const isSecondary = !schedule.isPrimary;
          const hasUnitBasedWarning = assetType === GearbeltAssetType.ToolsAndEquipment && 'hasUnitBasedTrigger' in schedule && schedule.hasUnitBasedTrigger;
          const showWarning = isSecondary || hasUnitBasedWarning;

          return (
            <TooltipProvider key={schedule.id}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <div className="flex" data-badge>
                    <ScheduleBadge schedule={schedule} disabled={showWarning} className="text-xs font-semibold" />
                  </div>
                </TooltipTrigger>

                <TooltipPortal>
                  <TooltipContent side="bottom" sideOffset={0} className="bg-default-text max-w-[200px] border-none p-1 text-xs text-white">
                    <TooltipArrow className="fill-default-text" />
                    {deploymentRules.length > 0 ? (
                      <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                        {isSecondary && (
                          <div>
                            <b>Warning:</b> {getScheduleLimitWarning(schedulePlanLimit, planType)}
                          </div>
                        )}

                        {hasUnitBasedWarning && (
                          <div>
                            <b>Warning:</b> Schedules with custom units are not allowed for Tools & Equipment.
                          </div>
                        )}

                        <div>
                          <b>Rules:</b>

                          <ul className="list-disc pl-4">
                            {displayRules.map((rule) => (
                              <li key={rule.id} className="hover:underline">
                                <Link className="block w-full truncate" to={getWorkspaceRoute(`/deployments/${rule.id}`)}>
                                  {rule.name}
                                </Link>
                              </li>
                            ))}
                            {remainingRules > 0 && <li>+{remainingRules} more...</li>}
                          </ul>
                        </div>
                      </div>
                    ) : (
                      <span>No deployment rules</span>
                    )}
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
      {hiddenCount > 0 && <span className="text-secondary-text text-sm">+{hiddenCount} more...</span>}
    </div>
  );
};

function getFormTypeText(formType: FormType) {
  return match(formType)
    .with(FormType.InspectionForm, () => 'inspection')
    .with(FormType.RepairForm, () => 'repair')
    .with(FormType.ScheduleForm, () => 'schedule')
    .otherwise(() => 'unknown');
}
